import { Box, Typography } from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterModel,
  GridLogicOperator,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUpdateDeal } from 'api/aws/useEntityUpdate'
import { useMassUpdate } from 'api/aws/useMassUpdate'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import AccountingDrawer from 'components/Accounting/AccountingDrawer'
import { AccountingGridFooter } from 'components/Accounting/AccountingGridFooter'
import { billingViewState, churnViewState, invoiceViewState } from 'components/Accounting/viewStates'
import { renderAccountingColumns } from 'components/DataGrid/createColumns/renderAccountingColumns'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { getMtbRowClassNames } from 'components/DataGrid/operators/getMtbRowClassNames'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import dayjs from 'dayjs'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Deal, DynamicMassUpdateData, OrganizationTableItem, Task } from 'types'
import { defaultColumnOrder } from './defaultModels'

type ProcessedDealTypes = OrganizationTableItem[] | Deal[]

const defaultSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }]
const defaultFilterModel: GridFilterModel = {
  items: [],
  quickFilterValues: [],
}
const pinnedColumns = { left: ['__check__', 'name'], right: [] }
const AccountingPage = () => {
  const { PK } = useParams()
  const isAccountingDetail = Boolean(PK)
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const { syncDealsCache, syncDealFields } = useSyncQueryCache()
  const apiRef = useGridApiRef()
  const { handleMassUpdate: handleMassUpdateDeals, isUpdating } = useMassUpdate({
    entityType: 'deal',
    context: 'deals',
    apiRef,
  })
  const { isExpanded, toggleExpanded: _toggleExpanded } = useTreeExpansionPersistence('mtb')
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()
  const [_isDrawerOpen, setIsDrawerOpen] = useState(false)

  const { mutate: updateDeal } = useUpdateDeal()
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('mtbDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const [massUpdateData, setMassUpdateData] = useState<DynamicMassUpdateData>({})
  const [selectedView, setSelectedView] = useState(() => {
    const savedView = localStorage.getItem('accountingSelectedView')
    return savedView ? JSON.parse(savedView) : 'invoice'
  })
  /////////////////////////////////////////////////////////////////////////////////
  ////////////////////////// Mtb State ////////////////////////////////////////////
  const [_selectedDeal, setSelectedDeal] = useState<OrganizationTableItem | Task>(null)
  const [selectedMassData, setSelectedMassData] = useState<Task[]>([])
  const [selectedMonthlyTask, setSelectedMonthlyTask] = useState<Task | null>(null)
  ////////////////////////////////////////////////////////////////////////////////
  const {
    deals: allDeals,
    organizations: allOrgs,
    products,
    totalsByMonth,
    allTasks,
    commentsTable,
    isItemsFetching,
    isItemsLoading,
  } = useEntitiesFromSearch()
  const currentMonth = dayjs().format('YYYY-MM')
  const [monthFilter, setMonthlyFilter] = useState(currentMonth ?? '')
  // const { data: taskData, isLoading: isTasksLoading } = useMonthlyTasks({})
  // const tasks = Array.isArray(taskData) ? taskData : taskData?.tasks ?? []
  const tasks = useMemo(() => {
    if (allTasks?.length > 0) {
      const monthlyTasks = allTasks?.filter(task => task?.SK?.startsWith('MONTHLY_'))
      return monthlyTasks
    }
  }, [allTasks])
  const organizations = useMemo(() => {
    if (!allOrgs || !allDeals) return []
    const activeStatuses = ['Active', 'In Progress', 'Month To Month', '30 Day Notice', 'Active (system)', 'Pending']
    const orgsWithActiveDeals = new Set(
      allDeals.filter(deal => activeStatuses.includes(deal.dealStatus)).flatMap(deal => deal.organizations || [])
    )
    return allOrgs.filter(org => orgsWithActiveDeals.has(org.org_uuid))
  }, [allOrgs, allDeals])
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { data: contacts, isLoading: isContactsLoading } = useContactsItems()

  ////////////////////////////////////////////////////////////////////////////////////////////
  const orgByPk = useMemo(() => new Map((allOrgs ?? []).map(o => [o.PK, o])), [allOrgs])
  const orgByUuid = useMemo(() => new Map((allOrgs ?? []).map(o => [o.org_uuid, o])), [allOrgs])

  const getOrgFromDeal = (deal: OrganizationTableItem) => {
    // try several shapes
    const ids = Array.isArray(deal.organizations) ? deal.organizations : []
    const first = ids[0]

    // If the deal stored PK directly (e.g. "ORG_123")
    if (typeof first === 'string' && first.startsWith('ORG_')) {
      return orgByPk.get(first) ?? null
    }

    // If the deal stored UUID (e.g. "123")
    if (typeof first === 'string') {
      return orgByPk.get(`ORG_${first}`) || orgByUuid.get(first) || null
    }

    // Fallbacks if you also store org_uuid/PK in other props
    // return orgByPk.get(deal.orgPK) || orgByUuid.get(deal.org_uuid) || null;

    return null
  }
  const processedDeals: ProcessedDealTypes = useMemo(() => {
    if (!allDeals || !allOrgs) return []

    return allDeals
      .map(deal => {
        const organization = getOrgFromDeal(deal)
        const orgName = organization?.name ?? 'Unknown Organization'

        const contractedById = Array.isArray(organization?.contractedBy) ? organization.contractedBy[0] : null
        const contractedBy = contractedById
          ? orgByPk.get(`ORG_${contractedById}`) || orgByUuid.get(contractedById)
          : null

        const primaryContact = contacts?.find(c => c.contacts_uuid === organization?.primaryContact?.[0]) ?? null
        const billingContact = contacts?.find(c => c.contacts_uuid === organization?.billingContact?.[0]) ?? null

        const comments = commentsTable
          ?.filter(c => c?.dealSK === deal?.SK)
          ?.filter(c => ['deals', 'accounting'].includes(c?.context))
        const count = comments?.length ?? 0

        return {
          ...deal,
          rowId: deal?.SK,
          organization,
          orgName, // <- add this
          contractedBy,
          primaryContact,
          billingContact,
          count,
          amId: users?.find(u => u?.user_uuid === organization?.AM?.[0]) ?? null,
        }
      })
      .filter(d => (d as any).stage === 'Deal Won')
  }, [allDeals, allOrgs, contacts, commentsTable, users, orgByPk, orgByUuid])
  // const processedDeals: ProcessedDealTypes = useMemo(() => {
  //   if (!allDeals || !organizations || !allOrgs) return []
  //   return allDeals
  //     .map((deal: NewDeal) => {
  //       const organization = organizations.find(
  //         org => org.PK === (Array.isArray(deal.organizations) ? `ORG_${deal.organizations[0]}` : null)
  //       )
  //       const contractedById =
  //         organization && Array.isArray(organization.contractedBy) ? organization.contractedBy[0] : null
  //       const contractedBy = allOrgs?.find(o => o?.PK === `ORG_${contractedById}`)
  //       const primaryContact = contacts?.find(contact => contact.contacts_uuid === organization?.primaryContact?.[0])
  //       const billingContact = contacts?.find(contact => contact.contacts_uuid === organization?.billingContact?.[0])
  //       const comments = commentsTable
  //         ?.filter(c => c?.dealSK && c?.dealSK === deal?.SK)
  //         .filter(c => ['deals', 'accounting']?.includes(c?.context))
  //       const count = comments?.length ?? 0
  //       return {
  //         ...deal,
  //         rowId: deal?.SK,
  //         //   contractedBy: organization?.contractedBy?.[0] || null,
  //         amId: users?.find(user => user?.user_uuid === organization?.AM?.[0]),
  //         organization: organization ?? null,
  //         contractedBy: contractedBy,
  //         primaryContact: primaryContact ?? null,
  //         billingContact: billingContact ?? null,
  //         count: count,
  //       }
  //     })
  //     .filter(deal => deal.stage === 'Deal Won')
  // }, [allDeals, organizations, allOrgs, users, contacts])
  const openDrawer = () => setIsDrawerOpen(true)
  const closeDrawer = () => setIsDrawerOpen(false)
  const clickEditStart = (params: GridRenderCellParams) => {
    // again, use the Set’s size
    if (params?.row?.SK?.startsWith('PRODUCT_')) return
    if (apiRef?.current?.state?.rowSelection.ids.size <= 1) {
      params.api.startCellEditMode({
        id: params.id,
        field: params.field,
      })
    }
  }
  const handleRowSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    if (Array.from(rowSelectionModel?.ids).length === 1) {
      const newDeal = allDeals.find(deal => deal.deal_uuid === Array.from(rowSelectionModel?.ids)[0])
      setSelectedDeal(newDeal)
      setMassUpdateData({})
    } else {
      setSelectedDeal(null)
    }
  }
  ///////////////////////////////////////////////// Row Updates //////////////////////////////////////////////////////////////
  const handleUpdate = async (PK, SK, updatedRow, updatedData) => {
    try {
      // Call the mutation with the deal ID and the updated data
      updateDeal(
        //@ts-ignore
        { PK, SK, dealData: updatedData },
        {
          onSuccess: () => {
            syncDealsCache(queryClient, updatedData)
            syncDealFields(queryClient, updatedRow, [{ ...updatedData }])
            showSnackbar(
              <Box sx={{ display: 'flex', alignItems: 'center' }} gap={1}>
                <Typography sx={{ color: 'text.primary', fontWeight: 'bold' }}>{updatedRow?.name || ''}</Typography>
                <Typography>Updated successfully</Typography>
              </Box>,
              'success'
            )
            console.log('Successfully updated deal')
          },
          onError: error => {
            console.error('Error updating deal', error)
          },
        }
      )

      // Optionally show a success message or perform other actions
      console.log(`Deal ${SK} updated successfully with`, updatedData)
    } catch (error) {
      // Handle errors (e.g., show an error message)
      console.error(`Failed to update deal ${SK}:`, error)
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const handleDealClick = deal => {
    // console.log('Deal Clicked! ', deal)
    setSelectedDeal(deal)
    openDrawer()
    navigate(`/accounting/${deal?.PK}/${deal?.SK}/details`)
  }
  const handleClose = () => {
    closeDrawer()
    setSelectedDeal(null)
    navigate(`/accounting`)
  }
  const processRowUpdate = async (updatedRow: OrganizationTableItem, oldRow) => {
    if (updatedRow.invoiceStatus !== oldRow.invoiceStatus) {
      // Call the centralized update function
      await handleUpdate(updatedRow.PK, updatedRow.SK, updatedRow, { invoiceStatus: updatedRow.invoiceStatus })
    } else if (updatedRow.billingStatus !== oldRow.billingStatus) {
      await handleUpdate(updatedRow.PK, updatedRow.SK, updatedRow, { billingStatus: updatedRow.billingStatus })
    }
    console.log('updatedRow', updatedRow)
    // Optimistically update the state
    const updatedDeals = allDeals.map(row =>
      row.id === updatedRow.id
        ? { ...row, invoiceStatus: updatedRow.invoiceStatus, billingStatus: updatedRow.billingStatus }
        : row
    )
    syncDealsCache(queryClient, updatedRow)

    return updatedRow // Return the updated row for the grid
  }
  const columns = useMemo(() => {
    return renderAccountingColumns(users, processedDeals, density, handleDealClick, clickEditStart) as GridColDef[]
  }, [users, processedDeals, density, handleDealClick, clickEditStart])
  const allRowIds = useMemo(() => {
    if (apiRef.current) return apiRef.current.getAllRowIds()
  }, [apiRef.current])
  const getRowClassNameWrapper = (params: GridRowClassNameParams, rowSelectionModel: GridRowSelectionModel) =>
    getMtbRowClassNames(params, rowSelectionModel, allRowIds)
  const defaultColumnWidths = Object.fromEntries(
    columns
      .filter(col => typeof col.width === 'number') // only include columns that define a width
      .map(col => [col.field, col.width])
  )
  const clearFilters = () => {
    setMonthlyFilter(currentMonth ?? '')
  }
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity)
  }

  const handleChangeView = (
    newView: string,
    setFilterModel: Dispatch<SetStateAction<GridFilterModel>>,
    setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>
  ) => {
    if (newView === 'invoice') {
      const newInvoiceFilters = [
        { id: `Active-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: 'Active' },
        { id: `MonthToMonth-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: 'Month To Month' },
        { id: `30DayNotice-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: '30 Day Notice' },
        { id: `InProgress-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: 'In Progress' },
      ]
      // Set the new filter model replacing any previous filters
      setFilterModel({ logicOperator: GridLogicOperator.Or, items: newInvoiceFilters })

      setColumnVisibilityModel(invoiceViewState)
      localStorage.setItem('accountingColumnVisibilityModel', JSON.stringify(invoiceViewState))
    } else if (newView === 'billing') {
      // For billing, clear filters or set billing-specific ones
      setFilterModel({ items: [], quickFilterValues: [] })
      setColumnVisibilityModel(billingViewState)
      localStorage.setItem('accountingColumnVisibilityModel', JSON.stringify(billingViewState))
    } else if (newView === 'churn') {
      const newChurnFilters = [
        { id: `Cancelled-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: 'Cancelled' },
        { id: `30DayNotice-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: '30 Day Notice' },
        { id: `Churned-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: 'Churned' },
        { id: `Ended-${Date.now()}`, field: 'dealStatus', operator: 'contains', value: 'Ended' },
      ]
      // Replace existing filters with churn-specific filters
      setFilterModel({ logicOperator: GridLogicOperator.Or, items: newChurnFilters })

      setColumnVisibilityModel(churnViewState)
      localStorage.setItem('accountingColumnVisibilityModel', JSON.stringify(churnViewState))
    }
  }
  const handleMassUpdateChange = e => {
    const { name, value } = e.target
    setMassUpdateData(prev => ({ ...prev, [name]: value }))
  }

  const FooterWrapper = ({ rowSelectionModel }: { rowSelectionModel: GridRowSelectionModel }) => {
    return (
      <AccountingGridFooter
        massUpdateData={massUpdateData}
        setMassUpdateData={setMassUpdateData}
        rowSelectionModel={rowSelectionModel}
        onMassUpdateChange={handleMassUpdateChange}
        handleMassUpdate={handleMassUpdateDeals}
      />
    )
  }
  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={processedDeals}
        apiRef={apiRef}
        context={'accounting'}
        isExpanded={isExpanded}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={invoiceViewState}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnWidths={defaultColumnWidths}
        defaultColumnOrder={defaultColumnOrder}
        pinnedColumns={pinnedColumns}
        monthFilter={monthFilter}
        onChangeDensity={changeDensity}
        onChangeView={handleChangeView}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        // onRowClick={handleRowClick}
        onClearFilters={clearFilters}
        onProcessRowUpdate={processRowUpdate}
        getRowClassName={getRowClassNameWrapper}
        toolbarComponent={GlobalGridToolbar}
        FooterComponent={FooterWrapper}
        treeData={false}
        gridContainerSx={{
          position: 'relative',
          pt: pinned ? '60px' : 0.8,
          transition: 'all 0.3s ease', // or .5s, etc.
          width: '100%',
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      />
      {isAccountingDetail && (
        <>
          <AccountingDrawer open={isAccountingDetail} onClose={handleClose} />
        </>
      )}
    </>
  )
}
export default AccountingPage
